// Package imports:
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
import './TradePage.scss';
// Component imports:
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TradeSummary from './TradeSummary';
import OrderBookSnapshot from '../order/OrderBookSnapshot';
import RelatedTrades from './RelatedTrades';
import RelatedAlerts from '../order/RelatedAlerts';
// Service imports:
import { turnRequestedToData } from '../../services/utils';
import { sortTickDataWithBook, sortTickData } from '../../services/sortUtils';
// Action imports:
import { requestPublicOrders, requestPublicTrades, requestPublicOpenClosingPrice } from '../../redux-store/actions/publicActions';
import { requestAlerts } from '../../redux-store/actions/alertActions';
import { setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { PublicTradeTickData } from '../../types/api/PublicTypes';
import { BY_LEVEL_SYMBOLS } from '../../types/api/Types';

interface IOwnProps {
    symbol: string;
    tradeId: string;
    date: string;
}
type ReduxProps = ConnectedProps<typeof connector>;
type Props = ReduxProps & IOwnProps;

const TradePage: React.FC<Props> = ({
    symbol,
    tradeId,
    date,
    accessTokenV2,
    publicOrderDoubleMap,
    publicTradeDoubleMap,
    publicOpenClosingPrice,
    alertsByDate,
    autoComplete,
    requestPublicOrders,
    requestPublicTrades,
    requestPublicOpenClosingPrice,
    requestAlerts,
    setSelectedTick
}) => {
    const isSymbolByLevel = useMemo(() => BY_LEVEL_SYMBOLS.includes(symbol), [symbol]);
    const [securityType, setSecurityType] = useState<'share' | 'bond' | 'derivative' | 'etf' | null>(null);

    useEffect(() => {
        document.title = `Trade ${tradeId} - ${symbol} | KODIAK Sigti`;
    }, [symbol, tradeId]);

    // Reset selected tick on mount / navigation.
    useLayoutEffect(() => {
        setSelectedTick(null);
    }, [symbol, tradeId, date, setSelectedTick]);

    // Security Type Effect.
    useEffect(() => {
        if (
            securityType === null
            && autoComplete !== undefined
            && autoComplete !== 'waiting'
            && !(autoComplete instanceof Error)
        ) {
            if ('bonds' in autoComplete) {
                const { bonds, shares, derivatives, etf } = autoComplete;
                if (bonds.find(bond => bond.symbol === symbol)) setSecurityType('bond');
                else if (shares.find(share => share.symbol === symbol)) setSecurityType('share');
                else if (derivatives.find(der => der.symbol === symbol)) setSecurityType('derivative');
                else if (etf.find(fund => fund.symbol === symbol)) setSecurityType('etf');
            }
        }
    }, [securityType, autoComplete, symbol]);

    // Public Orders (for book reconstruction).
    useEffect(() => {
        if (!isSymbolByLevel && publicOrderDoubleMap?.[symbol]?.[date] === undefined) {
            requestPublicOrders(symbol, date, accessTokenV2);
        }
    }, [publicOrderDoubleMap, symbol, date, accessTokenV2, isSymbolByLevel, requestPublicOrders]);

    // Public Trades.
    useEffect(() => {
        if (publicTradeDoubleMap?.[symbol]?.[date] === undefined) {
            requestPublicTrades(symbol, date, accessTokenV2);
        }
    }, [publicTradeDoubleMap, symbol, date, accessTokenV2, requestPublicTrades]);

    // Alerts.
    useEffect(() => {
        const alertInfo = alertsByDate[date];
        if (alertInfo === undefined) {
            requestAlerts(accessTokenV2, date, date);
        }
    }, [alertsByDate, date, accessTokenV2, requestAlerts]);

    // Open/Closing Price (OHLC).
    useEffect(() => {
        if (publicOpenClosingPrice?.[symbol]?.[date]?.[date] === undefined) {
            requestPublicOpenClosingPrice(symbol, date, date);
        }
    }, [publicOpenClosingPrice, symbol, date, requestPublicOpenClosingPrice]);

    /* Transform data */

    // All public orders for the day (with book state).
    const { sortedPublicOrderTickData } = useMemo(() => {
        const publicOrdersData = publicOrderDoubleMap?.[symbol]?.[date];
        const publicOrders = turnRequestedToData(publicOrdersData, []);
        const sortedPublicOrderTickData = sortTickDataWithBook(publicOrders);
        return { sortedPublicOrderTickData };
    }, [publicOrderDoubleMap, symbol, date]);

    // All public trades for the day.
    const sortedPublicTradeTickData = useMemo(() => {
        const publicTradesData = publicTradeDoubleMap?.[symbol]?.[date];
        const publicTrades = turnRequestedToData(publicTradesData, []);
        return sortTickData(publicTrades);
    }, [publicTradeDoubleMap, symbol, date]);

    // Find the specific trade.
    const trade: PublicTradeTickData | null = useMemo(() => {
        return sortedPublicTradeTickData.find(
            t => t.trade_id !== null && String(t.trade_id) === String(tradeId)
        ) ?? null;
    }, [sortedPublicTradeTickData, tradeId]);

    // Set the trade as selected tick for book computation.
    useLayoutEffect(() => {
        if (trade !== null) {
            setSelectedTick(trade);
        }
    }, [trade, setSelectedTick]);

    // Non-standard trades are negotiated off-book; book reconstruction is not relevant.
    const isNonStandard = useMemo(() =>
        trade !== null && trade.update_code === 'non_standard'
    , [trade]);

    // OHLC data.
    const ohlcData = useMemo(() => {
        const ohlcRequested = publicOpenClosingPrice?.[symbol]?.[date]?.[date];
        const ohlcArray = turnRequestedToData(ohlcRequested, []);
        return ohlcArray.length > 0 ? ohlcArray[0] : null;
    }, [publicOpenClosingPrice, symbol, date]);

    // Book state before and after the trade (by timestamp cutoff in order data).
    const { bookBefore, bookAfter } = useMemo(() => {
        if (trade === null || sortedPublicOrderTickData.length === 0) {
            return {
                bookBefore: [] as typeof sortedPublicOrderTickData,
                bookAfter: [] as typeof sortedPublicOrderTickData
            };
        }

        const tradeTimestamp = trade.timestamp ? new Date(trade.timestamp).getTime() : 0;

        // bookBefore: order events with timestamp strictly BEFORE the trade.
        // These orders were still in the book when the trade was agreed.
        const beforeIndex = sortedPublicOrderTickData.findIndex(({ tick }) =>
            tick.timestamp !== null && new Date(tick.timestamp).getTime() >= tradeTimestamp
        );
        const beforeIdx = beforeIndex === -1 ? sortedPublicOrderTickData.length : beforeIndex;

        // bookAfter: order events with timestamp strictly AFTER the trade.
        // This includes Deleted events at the trade timestamp (orders removed by the trade).
        const afterIndex = sortedPublicOrderTickData.findIndex(({ tick }) =>
            tick.timestamp !== null && new Date(tick.timestamp).getTime() > tradeTimestamp
        );
        const afterIdx = afterIndex === -1 ? sortedPublicOrderTickData.length : afterIndex;

        return {
            bookBefore: sortedPublicOrderTickData.slice(0, beforeIdx),
            bookAfter: sortedPublicOrderTickData.slice(0, afterIdx)
        };
    }, [sortedPublicOrderTickData, trade]);

    // The order IDs of the orders fully consumed by this trade.
    // We require Deleted at the exact trade timestamp, at the trade price,
    // with the exact trade volume — this combination is unique enough to
    // reliably identify the matched order without false positives.
    // Partial fills (Update events) are not highlighted as they cannot be
    // reliably distinguished from unrelated updates at the same millisecond.
    const tradedOrderIds = useMemo((): string[] => {
        if (trade === null) return [];
        return sortedPublicOrderTickData
            .filter(({ tick }) =>
                tick.update_code === 'Deleted' &&
                tick.timestamp !== null &&
                tick.timestamp === trade.timestamp &&
                tick.price === trade.price &&
                tick.volume === trade.volume
            )
            .map(({ tick }) => tick.order_id !== null ? String(tick.order_id) : '')
            .filter(id => id !== '');
    }, [sortedPublicOrderTickData, trade]);

    // Related alerts for this trade.
    const relatedAlerts = useMemo(() => {
        const alertsData = alertsByDate?.[date];
        const alerts = turnRequestedToData(alertsData, []);
        return alerts.filter(alert =>
            alert.symbol === symbol && (
                (alert.trade_id !== null && String(alert.trade_id) === String(tradeId))
            )
        );
    }, [alertsByDate, date, symbol, tradeId]);

    // Other trades by same participants.
    const relatedTrades = useMemo(() => {
        if (trade === null) return [];
        const buyer = trade.trade_buyer;
        const seller = trade.trade_seller;
        return sortedPublicTradeTickData.filter(t =>
            (buyer && (t.trade_buyer === buyer || t.trade_seller === buyer)) ||
            (seller && (t.trade_buyer === seller || t.trade_seller === seller))
        );
    }, [sortedPublicTradeTickData, trade]);

    // Loading state.
    const isLoading = (
        publicTradeDoubleMap?.[symbol]?.[date] === 'waiting'
        || publicTradeDoubleMap?.[symbol]?.[date] === undefined
    );

    const isOrderDataLoading = (
        publicOrderDoubleMap?.[symbol]?.[date] === 'waiting'
        || publicOrderDoubleMap?.[symbol]?.[date] === undefined
    );

    return (
        <Container id='TradePage' fluid>
            {isLoading ? (
                <Row className='TradePageHeader'>
                    <Col md={12}>
                        <h4>Trade Execution Analysis</h4>
                        <div className='LoadingMessage'>Loading trade data...</div>
                    </Col>
                </Row>
            ) : trade === null ? (
                <Row className='TradePageHeader'>
                    <Col md={12}>
                        <div className='NotFoundMessage'>
                            <h5>Trade not found</h5>
                            <p>No trade found with ID <strong>{tradeId}</strong> on {date} for {symbol}.</p>
                        </div>
                    </Col>
                </Row>
            ) : (
                <>
                    {/* Header / Summary */}
                    <Row className='TradePageHeader'>
                        <Col md={12}>
                            <TradeSummary
                                tradeId={tradeId}
                                symbol={symbol}
                                date={date}
                                trade={trade}
                                ohlcData={ohlcData}
                                securityType={securityType}
                            />
                        </Col>
                    </Row>

                    {/* Book Before & After — shows order book state at trade time (useful for spread validation on non-standard trades) */}
                    {!isSymbolByLevel && (
                        <Row className='BookRow'>
                            <Col xl={6} md={6} xs={12} className='BookCol'>
                                <h5 className='SectionTitle'>Book Before</h5>
                                {isOrderDataLoading ? (
                                    <div className='LoadingMessage'>Loading order book...</div>
                                ) : (
                                    <OrderBookSnapshot
                                        sortedPublicOrderTickData={bookBefore}
                                        highlightOrderIds={tradedOrderIds}
                                    />
                                )}
                            </Col>
                            <Col xl={6} md={6} xs={12} className='BookCol BookAfterCol'>
                                <h5 className='SectionTitle'>Book After</h5>
                                {isOrderDataLoading ? (
                                    <div className='LoadingMessage'>Loading order book...</div>
                                ) : (
                                    <OrderBookSnapshot
                                        sortedPublicOrderTickData={bookAfter}
                                    />
                                )}
                            </Col>
                        </Row>
                    )}

                    {/* Related Trades & Related Alerts */}
                    <Row className='AnalyticsRow'>
                        <Col xl={6} md={12} xs={12}>
                            <RelatedTrades
                                trades={relatedTrades}
                                buyer={trade.trade_buyer}
                                seller={trade.trade_seller}
                                currentTradeId={tradeId}
                                symbol={symbol}
                                date={date}
                            />
                        </Col>
                        <Col xl={6} md={12} xs={12}>
                            <RelatedAlerts
                                relatedAlerts={relatedAlerts}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </Container>
    );
};

function mapStateToProps({ dashboardReducer, loginReducer, publicReducer, alertReducer }: RootState) {
    const { publicOrderDoubleMap, publicTradeDoubleMap, publicOpenClosingPrice } = publicReducer;
    const { autoComplete } = dashboardReducer;
    const { accessTokenV2 } = loginReducer;
    const { alertsByDate } = alertReducer;
    return {
        accessTokenV2,
        publicOrderDoubleMap,
        publicTradeDoubleMap,
        publicOpenClosingPrice,
        alertsByDate,
        autoComplete
    };
}

const connector = connect(mapStateToProps, {
    requestPublicOrders,
    requestPublicTrades,
    requestPublicOpenClosingPrice,
    requestAlerts,
    setSelectedTick
});
export default connector(TradePage);
